/**
 * Mentetlen-változás megállapítása: az űrlap JELENLEGI állapota eltér-e a BETÖLTÖTT alapvonaltól.
 *
 * MIÉRT KÜLÖN, TISZTA FÜGGVÉNY: a „mentetlen adat" védelemnél a leggyakoribb hiba a HAMIS piszkos
 * állapot — a felhasználó semmit nem írt át, de a felület mégis rákérdez, hogy elveszik-e a
 * munkája. Egy-két ilyen után a kérdésen mindenki átkattint, és onnantól a védelem nem védelem.
 * A hamis piszkosság tipikus forrásai ebben a kódbázisban:
 *
 *  1. `undefined` vs `null` vs `''` — a szerver `null`-t ad (`hourlyRate`, `parentName`), a
 *     betöltő `|| ''`-ra vagy `|| undefined`-ra hozza, a mező `onChange`-e pedig megint másra
 *     (pl. `value ? parseFloat(value) : undefined`). Mindhárom UGYANAZT jelenti: „nincs megadva".
 *  2. Dátum ISO-alakja — a szerver `2026-09-20T00:00:00`-t ad, a dátumválasztó `2026-09-20`-at
 *     ír vissza. Ha a felhasználó UGYANAZT a napot választja újra, az nem módosítás.
 *  3. Tömb-sorrend — a halmaz-jellegű listáknál (preferált edzések, kijelölt résztvevők, napok)
 *     a be-/kipipálás a végére teszi az elemet, így a sorrend megváltozik, a TARTALOM nem.
 *     Ezekben a formokban egyetlen tömb sorrendje sem hordoz jelentést, ezért a tömböket
 *     MULTISZETTKÉNT hasonlítjuk (a sorrend nem számít, a darabszám igen).
 *  4. Hiányzó kulcs vs üres érték — `{ taxNumber: '' }` és `{}` ugyanaz.
 *  5. SZÁM a szerverről, SZÖVEG a mezőből [BL-1, 2026-09-13] — a betöltő a szerver `13`-át teszi
 *     az alapvonalba, a `type="number"` mező `onChange`-e viszont NYERS szöveget ír vissza
 *     (`e.target.value` → `"13"`). E szabály nélkül a `13 ≠ "13"` MINDIG igaz, tehát az
 *     „átírtam, majd visszaírtam az eredetit" állapot SOHA nem tudott tisztára térni: egyetlen
 *     leütés (a fókuszált number-inputon a görgő is elég) után a bezárás a dialógus végéig azt
 *     hazudta, hogy elveszik az adat. Mérve: `/api/venues` → `tableCount: 13`, 13→14→13.
 *  6. IDŐ „HH:mm" vs „HH:mm:ss" [BL-2, 2026-09-13] — a .NET `TimeSpan` `"09:00:00"`-t ad
 *     (mérve: `GET /api/coach-availability/{id}`), az idő-választó viszont `"09:00"`-t ír
 *     (`toTimeString().slice(0, 5)`). Ugyanaz az időpont, ugyanaz a mentendő érték.
 *
 * AMI VISZONT VALÓDI KÜLÖNBSÉG: a `0` és a `false` NEM üres (`requiredTables: 0`,
 * `isActive: false` érdemi érték), és a szám `0` nem egyenlő az üres mezővel.
 *
 * ⚠️ A szám-egyenértékűség SZÁNDÉKOSAN csak TÍPUS-ÁTHIDALÓ (szám ↔ szám-alakú szöveg). Két
 * SZÖVEG összevetése BÁJTHŰ marad, különben a vezető nullák eltűnnének a hasonlításból, és egy
 * adószám/telefonszám `"08123456789"` → `"8123456789"` átírása NÉMÁN „nem változásnak" tűnne —
 * az pedig a fordított irányú, súlyosabb kár (a védelem elhallgatná a valódi módosítást).
 *
 * ⚠️ AMIT EZ AZ ÁLLÍTÁS NEM FED [N-9, 2026-09-14]: a bájthűség csak a SZÖVEG↔SZÖVEG párra igaz.
 * A SZÁM↔SZÖVEG párban a vezető nulla ELTŰNIK — `isFormDirty({ t: '013' }, { t: 13 })` TISZTA,
 * mert a típus-áthidalás számként veti össze a kettőt (`13 === 13`). Ez a 5. pont ára: enélkül a
 * `type="number"` mezők „visszaírtam az eredetit" állapota sosem tudott tisztára térni. Élő
 * adatban ma nem elérhető (minden azonosító-szerű mező JSON-SZÖVEGként jön és szövegként megy
 * vissza, tehát szöveg↔szöveg párban van), de aki új mezőt kötne be SZÁM-alapvonallal és
 * SZÖVEG-bevitellel, annál a vezető nulla eltűnik a hasonlításból — ott a mezőt szöveg↔szöveg
 * párra kell hozni. (Őr-teszt: `formDirty.test.ts`, „a vezető nulla a SZÁM↔SZÖVEG párban…".)
 */

/** `undefined` / `null` / csak-whitespace szöveg → mind ugyanaz az „üres" jelentés. */
const EMPTY = '';

const MIDNIGHT_ISO = /^(\d{4}-\d{2}-\d{2})T00:00(?::00(?:\.0+)?)?Z?$/;

/** „09:00:00" (opcionálisan nulla törtmásodpercekkel) → „09:00". Nem-nulla másodperc MEGMARAD. */
const WHOLE_MINUTE_TIME = /^(\d{1,2}:\d{2}):00(?:\.0+)?$/;

/**
 * Szigorúan SZÁM-ALAKÚ szöveg. Ami NEM az: az üres string, a csak-whitespace, a `"12px"`, a
 * `"1e3"` — így a `Number('') === 0` csapda sem tud beszivárogni (az üres mező nem lesz `0`).
 */
const NUMERIC_TEXT = /^-?\d+(?:[.,]\d+)?$/;

/**
 * Egy érték összehasonlítható alakja. Rekurzív: objektumokból kulcs-rendezett objektum lesz
 * (az üres értékű kulcsok kimaradnak), tömbökből rendezett — tehát sorrend-független — lista.
 *
 * A TÍPUS MEGMARAD (a szám szám, a szöveg szöveg): a szám↔szöveg egyenértékűséget az
 * összehasonlítás végzi páronként (lásd `crossTypeNumberEqual`), nem ez a normalizálás — így egy
 * szöveg-szöveg összevetés sosem veszti el a vezető nullákat.
 */
export function normalizeFormValue(value: unknown): unknown {
  if (value === undefined || value === null) return EMPTY;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const midnight = MIDNIGHT_ISO.exec(trimmed);
    if (midnight) return midnight[1];
    const wholeMinute = WHOLE_MINUTE_TIME.exec(trimmed);
    return wholeMinute ? wholeMinute[1] : trimmed;
  }

  if (typeof value === 'number') {
    // A `NaN` az üres számmezőből keletkezik (`parseFloat('')`), tehát „nincs megadva".
    return Number.isNaN(value) ? EMPTY : value;
  }

  if (typeof value === 'boolean') return value;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? EMPTY : value.toISOString();
  }

  if (Array.isArray(value)) {
    // Multiszett: a sorrend nem számít (lásd a fejléc 3. pontját), a darabszám igen. A rendezés
    // kulcsa TÍPUS-FÜGGETLEN (`13` és `"13"` ugyanoda kerül), különben a szám↔szöveg páros
    // elcsúszna egymás mellől, és az elemenkénti összevetés hamis eltérést látna.
    return value
      .map((item) => normalizeFormValue(item))
      .map((item) => ({ key: canonicalKey(item), item }))
      .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))
      .map((entry) => entry.item);
  }

  if (typeof value === 'object') {
    const source = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    Object.keys(source)
      .sort()
      .forEach((key) => {
        const normalized = normalizeFormValue(source[key]);
        // Az üres értékű kulcs = nincs kulcs (lásd a fejléc 1. és 4. pontját).
        if (normalized === EMPTY) return;
        out[key] = normalized;
      });
    return out;
  }

  // Függvény / szimbólum űrlapállapotban nem fordul elő; az utolsó mentsvár az azonosság.
  return String(value);
}

/** A tömb-rendezés TÍPUS-FÜGGETLEN kulcsa — kizárólag a sorrendhez, az összevetéshez SOHA. */
function canonicalKey(value: unknown): string {
  const asNumber = numericValue(value);
  if (asNumber !== null) return `#${asNumber}`;
  if (Array.isArray(value)) return `[${value.map(canonicalKey).join(',')}]`;
  if (value !== null && typeof value === 'object') {
    const source = value as Record<string, unknown>;
    return `{${Object.keys(source)
      .sort()
      .map((key) => `${key}:${canonicalKey(source[key])}`)
      .join(',')}}`;
  }
  return `${typeof value}:${String(value)}`;
}

/** Szám-értéke az adott értéknek, ha van; `null`, ha nincs (üres mező, szöveg, logikai érték). */
function numericValue(value: unknown): number | null {
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    /*
     * A tizedes-vessző a magyar bevitel miatt van benne („2800,5" a számmezőben).
     *
     * ⚠️ FOGALOM-ÜTKÖZÉS, KIMONDVA [NB-6, 2026-09-14]: a `utils/money.ts` szerint a `2,800`
     * KÉTEZER-NYOLCSZÁZ (a vessző EZRES-elválasztó a magyar írásmódban), itt viszont a `2,800`
     * → 2.8 (a vessző TIZEDES-jel). UGYANAZ a karakter, két ellentétes jelentés.
     *
     * MIÉRT NEM ÜTKÖZIK A GYAKORLATBAN, ÉS MIÉRT MARAD ÍGY:
     *  1. Ez a szabály KIZÁRÓLAG ÖSSZEVETÉSRE szolgál („piszkos-e az űrlap?"), SOHA nem állít
     *     elő mentendő értéket — a kimenet mindig a mező saját értelmezőjétől jön.
     *  2. PÉNZ-mezőn el sem érhető: a nyolc forint-mező 2026-09-14 óta CSAK számjegyet fogad
     *     (`money.ts` `FORINT_INPUT_SHAPE`), tehát vesszős szöveg nem tud bennük állni.
     *  3. A megmaradt `type="number"` mezők MIND darabszám/sorrend (asztalok száma, max.
     *     résztvevők, sorrend) vagy a SZÁNDÉKOSAN tizedes „Órák (opcionális)" — ezeken a
     *     tizedes-vesszős értelmezés a HELYES („1,5 óra"), nem pénz.
     * ⚠️ EZÉRT: ezt a szabályt NE használd pénz-BEVITEL értelmezésére — arra egyetlen hely van,
     * a `utils/money.ts` `parseForintInput`-ja.
     */
    return NUMERIC_TEXT.test(trimmed) ? Number(trimmed.replace(',', '.')) : null;
  }
  return null;
}

/**
 * TÍPUS-ÁTHIDALÓ szám-egyenértékűség: `13 ≡ "13"`, `20000 ≡ "20000"`.
 *
 * Amit SZÁNDÉKOSAN nem tesz: két SZÖVEGET nem hasonlít számként (`"013" ≠ "13"` marad, hogy a
 * vezető nulla ne tűnhessen el egy adószámból), és az üres mezőt nem teszi `0`-val egyenlővé
 * (`''` nem szám-alakú szöveg, tehát a `numericValue` `null`-t ad rá).
 */
function crossTypeNumberEqual(a: unknown, b: unknown): boolean {
  const aIsNumber = typeof a === 'number';
  const bIsNumber = typeof b === 'number';
  // Szám-szám és szöveg-szöveg: nincs engedmény (az azonosságot már az `===` elbírálta).
  if (aIsNumber === bIsNumber) return false;
  const aNum = numericValue(a);
  const bNum = numericValue(b);
  return aNum !== null && bNum !== null && aNum === bNum;
}

/** Két MÁR NORMALIZÁLT érték egyenértékűsége (szerkezetileg, a szám↔szöveg engedménnyel). */
function equivalentNormalized(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (crossTypeNumberEqual(a, b)) return true;

  const aIsArray = Array.isArray(a);
  const bIsArray = Array.isArray(b);
  if (aIsArray || bIsArray) {
    if (!aIsArray || !bIsArray) return false;
    if (a.length !== b.length) return false;
    return a.every((item, index) => equivalentNormalized(item, b[index]));
  }

  const aIsObject = a !== null && typeof a === 'object';
  const bIsObject = b !== null && typeof b === 'object';
  if (aIsObject && bIsObject) {
    const aRecord = a as Record<string, unknown>;
    const bRecord = b as Record<string, unknown>;
    const aKeys = Object.keys(aRecord);
    const bKeys = Object.keys(bRecord);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every(
      (key) =>
        Object.prototype.hasOwnProperty.call(bRecord, key) && equivalentNormalized(aRecord[key], bRecord[key])
    );
  }

  return false;
}

/**
 * Piszkos-e az űrlap: eltér-e a `current` a `baseline`-tól.
 *
 * `baseline == null` esetén SOHA nem piszkos: ez a „még nem töltött be" állapot. Így a betöltés
 * közbeni kezdőértékek (üres string-ek, alapértelmezések) nem számítanak felhasználói bevitelnek,
 * és a képernyő nem kezd el mentetlen adatot jelenteni, mielőtt bármi is lenne rajta.
 */
export function isFormDirty<T>(baseline: T | null | undefined, current: T): boolean {
  if (baseline === null || baseline === undefined) return false;
  return !equivalentNormalized(normalizeFormValue(baseline), normalizeFormValue(current));
}

/**
 * A megerősítő ablak egységes szövege a mentetlen adat elvetéséhez.
 *
 * KANONIKUS SZÖVEG-DÖNTÉS (2026-09-13): a CÍM az egész alkalmazásban „Mentetlen változás" — a
 * fül-/dátum-váltás őre is ezt használja, hogy a felhasználó ugyanarról a dologról ugyanazt a
 * fejlécet lássa. A gomb-feliratok a MŰVELETHEZ illeszkednek (itt: „Elvetés és bezárás"; a
 * váltásnál „Váltás"), a kilépő gomb mindenhol „Mégse".
 * `destructive: true`: a MEGERŐSÍTÉS dobja el a beírt adatot, tehát a veszélyes gomb a
 * megerősítés — ezért vörös, és a kezdő fókuszt a „Mégse" kapja (lásd `ConfirmProvider`),
 * hogy egy reflexből lenyomott Enter ne törölje a munkát.
 */
export function discardChangesConfirm(what: string) {
  return {
    title: 'Mentetlen változás',
    message: `A(z) ${what} mentetlen módosításokat tartalmaz. Ha most bezárod, a beírt adatok elvesznek.`,
    confirmLabel: 'Elvetés és bezárás',
    cancelLabel: 'Mégse',
    destructive: true,
  };
}
